import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useBrawlSession from '../hooks/useBrawlSession';
import BrawlStage from '../components/BrawlStage';
import BrawlControls from '../components/BrawlControls';
import FightOverlay, { useCountdown } from '../components/FightOverlay';
import { fetchCharacters } from '../../../inventory/store/characterSlice';
import { fetchMissions, fetchLair } from '../../../lair/store/lairSlice';
import { RewardChip } from '../../../lair/components/MissionsTab';
import { formatAmount } from '../../../lair/utils/missionMath';
import {
  MAX_MANA, ST, fighterSpeed, spellMeta, worldOf,
} from '../engine/constants';
import { normalizeLook } from '../../common/HeroFigure';
import { haptic, hapticNotify } from '../utils/haptics';
import '../styles/comix.css';
import { useTranslation } from 'react-i18next';
import { gameErrorText } from '../../common/gameErrors';

/**
 * Brawler mission (Comix Zone style PvE, SPEC_V141_GAMES.md §5): a run started from the Missions
 * tab, played over the same real-time WS engine as the Arena (`pve.join {runId}`).
 * 3 comic panels of enemy waves (page turn on `panel`), result with stars + rewards.
 */

const RESULT_DELAY_MS = 1600;
const NO_CHARACTERS = [];

function Stars({ n }) {
  return (
    <div className="cx-stars" aria-label={`${n} stars`}>
      {[1, 2, 3].map((i) => (
        <span key={i} className={`cx-star${i <= n ? ' is-on' : ''}`} style={{ animationDelay: `${0.25 * i}s` }}>★</span>
      ))}
    </div>
  );
}

function HeroBar({ meta, f }) {
  const { t } = useTranslation();
  const maxHp = Number(meta?.maxHp) || 1;
  const hp = Math.max(0, Number(f?.hp ?? maxHp));
  const pct = Math.max(0, Math.min(100, (hp / maxHp) * 100));
  const mana = Math.max(0, Math.min(100, ((Number(f?.mn) || 0) / MAX_MANA) * 100));
  return (
    <div className="cx-herobar">
      <div className="cx-herobar__name">{meta?.name || t('common.hero', 'Hero')}</div>
      <div className="cx-herobar__hp"><div style={{ width: `${pct}%` }} /><span>{Math.ceil(hp)}</span></div>
      <div className="cx-herobar__mana"><div className={mana >= 100 ? 'is-full' : ''} style={{ width: `${mana}%` }} /></div>
    </div>
  );
}

export default function BrawlerPage() {
  const { t } = useTranslation();
  const { runId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const characters = useSelector((s) => s.characters?.list) || NO_CHARACTERS;
  const missionsData = useSelector((s) => s.lair?.missions?.data);

  const run = useMemo(() => (missionsData?.runs || []).find((r) => String(r.id) === String(runId)), [missionsData, runId]);
  const character = useMemo(() => characters.find((c) => c.id === run?.characterId), [characters, run]);
  const heroStats = useMemo(
    () => (missionsData?.characters || []).find((c) => c.id === run?.characterId) || character,
    [missionsData, run, character],
  );
  const speed = heroStats ? fighterSpeed(heroStats) : 260;

  const session = useBrawlSession({ speed });
  const {
    match, panel, end, status, error, engine, start, socket,
  } = session;
  const [hud, setHud] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [turning, setTurning] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const turnTimer = useRef(null);
  const lastPanel = useRef(null);

  useEffect(() => {
    if (!characters.length) dispatch(fetchCharacters());
    if (!missionsData) dispatch(fetchMissions());
    start((sock) => sock.joinPve(runId));
    return () => clearTimeout(turnTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // page turn between panels
  useEffect(() => {
    if (!panel) return;
    if (lastPanel.current !== null && lastPanel.current !== panel.n) {
      setTurning(true);
      haptic('medium');
      clearTimeout(turnTimer.current);
      turnTimer.current = setTimeout(() => setTurning(false), 1500);
    }
    lastPanel.current = panel.n;
  }, [panel]);

  useEffect(() => {
    if (!end) return undefined;
    hapticNotify(end.winner === match?.you || Number(end.stars) > 0 ? 'success' : 'error');
    const id = setTimeout(() => setShowResult(true), RESULT_DELAY_MS);
    return () => clearTimeout(id);
  }, [end, match]);

  const me = match?.fighters?.find((f) => f.idx === match.you) || null;
  const ownFrame = hud?.f.find((f) => f.i === match?.you);
  const enemiesLeft = hud ? hud.f.filter((f) => f.i !== match?.you && f.st !== ST.DEAD && f.hp > 0).length : null;
  const startsAt = match ? match.receivedAt + (Number(match.startsInMs) || 0) : null;
  const countdownLeft = useCountdown(startsAt);
  // own look fallback (the server sends `look` for every fighter, §11.2)
  const looks = useMemo(() => {
    const l = normalizeLook(character);
    return match && l ? { [match.you]: l } : null;
  }, [match, character]);
  const world = useMemo(() => worldOf(match ? { ...match, mode: match.mode || 'PVE' } : null), [match]);
  const fighting = !!match && !end;

  const onEvent = useCallback((e) => {
    if (!match) return;
    if (e.e === 'hit' && e.v === match.you) haptic(e.crit ? 'heavy' : 'medium');
    if (e.e === 'boom') haptic('heavy');
    if (e.e === 'ko' && e.v !== match.you) haptic('rigid');
    if (e.e === 'land' && e.a === match.you) haptic('soft');
  }, [match]);

  const goBack = () => {
    socket()?.close();
    dispatch(fetchMissions());
    dispatch(fetchLair());
    navigate('/lair', { state: { tab: 'Missions' } });
  };

  const panelN = panel?.n ?? match?.panel?.n ?? 1;
  const panelTotal = panel?.total ?? match?.panel?.total ?? 3;
  const won = end && (end.winner === match?.you || Number(end.stars) > 0);
  const stars = Number(end?.stars) || 0;
  const banner = end
    ? (won ? t('arena.result.victory', 'VICTORY!') : t('arena.banner.ko', 'K.O.!'))
    : turning ? t('brawler.panel', 'PANEL {{n}}', { n: panelN }) : null;

  // ── result ────────────────────────────────────────────────────────────────
  if (end && showResult) {
    const rewards = end.rewards || [];
    return (
      <div className="cx-page">
        <div className={`cx-result ${won ? 'is-win' : 'is-loss'}`}>
          <div className="cx-caption cx-caption--big">{won ? t('brawler.to_be_continued', 'TO BE CONTINUED…') : t('brawler.the_end', 'THE END?')}</div>
          <div className="cx-result__title">{won ? t('mission.complete', 'MISSION COMPLETE') : t('mission.failed', 'MISSION FAILED')}</div>
          {won && <Stars n={stars} />}
          <div className="cx-result__rewards">
            {rewards.length === 0
              ? <span className="cx-muted">{t('mission.no_rewards', 'No rewards')}</span>
              : rewards.map((r, i) => (
                <RewardChip key={`${r.rewardType}-${r.resourceCode || r.moduleId || ''}-${i}`} reward={r} amount={`+${formatAmount(r.amount)}`} />
              ))}
          </div>
          <button type="button" className="app-btn app-btn--green" onClick={goBack}>{t('mission.back_to_lair', 'Back to Lair')}</button>
        </div>
      </div>
    );
  }

  // ── connecting / error ────────────────────────────────────────────────────
  if (!match) {
    const failed = status === 'failed' || !!error;
    return (
      <div className="cx-page">
        <div className="cx-center">
          <div className="cx-caption">{failed ? t('brawler.oh_no', 'OH NO!') : t('brawler.meanwhile', 'MEANWHILE…')}</div>
          <div className="cx-muted">
            {failed
              ? (error ? gameErrorText(t, error) : t('brawler.join_failed', 'Could not join the fight'))
              : t('brawler.entering', 'Entering the comic…')}
          </div>
          {failed
            ? <button type="button" className="app-btn app-btn--purple app-btn--sm" onClick={goBack}>{t('mission.back_to_lair', 'Back to Lair')}</button>
            : <div className="cx-spinner" />}
        </div>
      </div>
    );
  }

  // ── fight ─────────────────────────────────────────────────────────────────
  return (
    <div className="cx-page">
      <div className="cx-top">
        <button type="button" className="cx-flag" onClick={() => (fighting ? setConfirmLeave(true) : goBack())} aria-label={t('arena.give_up', 'Give up')}>✕</button>
        <div className="cx-caption">PANEL {panelN}/{panelTotal}</div>
        {enemiesLeft !== null && <div className="cx-enemies">👊 {enemiesLeft}</div>}
      </div>

      <div className={`cx-panel${turning ? ' is-turning' : ''}`} key={`panel-${panelN}`}>
        <div className="cx-panel__inner">
          <HeroBar meta={me} f={ownFrame} />
          <BrawlStage
            engine={engine}
            fighters={match.fighters}
            you={match.you}
            world={world}
            looks={looks}
            theme="comix"
            onHud={setHud}
            onEvent={onEvent}
          />
          <FightOverlay startsAt={startsAt} banner={banner} startLabel="FIGHT!" />
          {status === 'reconnecting' && !end && <div className="cx-bubble cx-bubble--warn">{t('pvp.reconnecting', 'Reconnecting…')}</div>}
          {status === 'failed' && !end && (
            <div className="cx-bubble cx-bubble--warn">
              Connection lost
              <button type="button" className="app-btn app-btn--purple app-btn--sm" onClick={goBack}>{t('common.back', 'Back')}</button>
            </div>
          )}
        </div>
        <div className="cx-panel__page" aria-hidden="true" />
      </div>

      <BrawlControls
        theme="comix"
        onInput={session.sendInput}
        onAct={session.sendAct}
        mana={ownFrame?.mn || 0}
        guard={ownFrame?.gd}
        spell={me ? spellMeta(me.spell, me.character) : null}
        disabled={!fighting || countdownLeft > 0 || turning}
      />

      {confirmLeave && (
        <div className="cx-confirm">
          <div className="cx-confirm__box">
            <div className="cx-caption">{t('arena.give_up_q', 'Give up?')}</div>
            <div className="cx-muted">{t('brawler.counts_failed', 'The mission counts as failed.')}</div>
            <div className="cx-confirm__actions">
              <button type="button" className="app-btn app-btn--purple app-btn--sm" onClick={() => setConfirmLeave(false)}>{t('arena.fight_on', 'Fight on')}</button>
              <button type="button" className="app-btn app-btn--stop app-btn--sm" onClick={() => { setConfirmLeave(false); socket()?.leave(); }}>{t('arena.give_up', 'Give up')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
